import React, { useEffect, useRef, useState } from 'react';
import host from '../../global';
import axios from 'axios';
import { Camera, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import Cookies from 'js-cookie';
import { getCurrentUser } from '../../utils/authSession';
import Loader from '../Loader';
import { useTheme } from '../../hooks/useTheme';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { toast } from 'react-toastify';

const getInputStyle = (theme) => ({
  width: '100%', padding: '11px 12px', fontSize: '14px',
  border: `1px solid ${theme === 'dark' ? 'rgba(75,85,99,0.5)' : '#d1d5db'}`,
  borderRadius: '6px',
  background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#fff',
  color: theme === 'dark' ? '#d1d5db' : '#111827',
  outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
  lineHeight: '1.5',
});

const Row = ({ label, hint, children, last, theme }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: '220px 1fr',
    gap: '16px 40px',
    padding: '20px 0',
    borderBottom: last ? 'none' : `1px solid ${theme === 'dark' ? 'rgba(75,85,99,0.5)' : '#e5e7eb'}`,
    alignItems: 'start',
  }}>
    <div>
      <p style={{ margin: '0 0 3px', lineHeight: '1.4' }}
        className="dark:text-gray-300 text-textPrimary text-sm font-medium">
        {label}
      </p>
      {hint && (
        <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.45' }}
          className="dark:text-[#8893C8] text-textSecondary">
          {hint}
        </p>
      )}
    </div>
    <div>{children}</div>
  </div>
);

const EditPersonalProfile = () => {
  const personalFileInputRef = useRef(null);
  const profile_photos_host = process.env.REACT_APP_PROFILE_PHOTOS_HOST;

  const [personalData, setPersonalData] = useState({
    name: '', email: '', contact: '',
    profileImage: '', profileImageFile: null, deleteProfileImage: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [isLoading, setIsLoading] = useState(true);
  const [defaultCountry, setDefaultCountry] = useState("IN");
  const { theme } = useTheme();
  const accessToken = Cookies.get('accessToken');

  useEffect(() => {
    const detectCountry = async () => {
      try {
        const { data } = await axios.get("https://ipwho.is/", { timeout: 3000 });
        const code = data.country_code?.toUpperCase();
        if (code) setDefaultCountry(code);
      } catch (e) {
        console.warn("IP geolocation failed, using fallback");
      }
    };
    detectCountry();
  }, []);

  useEffect(() => {
    const fetchPersonalData = async () => {
      try {
        const res = await fetch(`${host}/getadmindetails`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const data = await res.json();
        const { userId } = await getCurrentUser();
        setPersonalData({
          name: data.name || '',
          email: data.email || '',
          contact: data.contact || '',
          profileImage: data.profile
            ? `${profile_photos_host}/OrganizationAdmins/${userId}_${data.profile}` : '',
          profileImageFile: null,
          deleteProfileImage: false,
        });
      } catch (err) {
        console.error(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPersonalData();
  }, [accessToken, profile_photos_host]);

  const handlePersonalSubmit = async (e) => {
    if (e) e.preventDefault();
    setIsSubmitting(true);
    setMessage({ text: '', type: '' });

    const fd = new FormData();
    fd.append('org_admin_name', personalData.name);
    fd.append('org_admin_email', personalData.email);
    fd.append('org_admin_contact', personalData.contact);
    if (personalData.profileImageFile) fd.append('profile', personalData.profileImageFile);
    if (personalData.deleteProfileImage) fd.append('deleteProfileImage', 'true');

    try {
      const res = await fetch(`${host}/org-admin/updateadminprofile`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${accessToken}` },
        body: fd,
      });
      if (res.ok) {
        toast.success("Profile updated successfully");
        setTimeout(() => window.location.reload(), 1200);
      } else {
        toast.error("Failed to Update Profile");
      }
    } catch {
      toast.error("Failed to Update Profile");
    }
    setIsSubmitting(false);
  };

  if (isLoading) return <Loader />;

  return (
    <>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        input:focus, select:focus, textarea:focus { outline: none; border-color: #9ca3af !important; }
        .PhoneInput { display: flex; align-items: center; gap: 8px; }
        .PhoneInputInput { flex: 1; background: transparent; border: none; outline: none; font-size: 14px; font-family: inherit; padding: 11px 4px; }
        .PhoneInputInput::placeholder { color: #9ca3af; }
        .PhoneInputCountry { display: flex; align-items: center; }
      `}</style>

      <div className="min-h-screen flex-1 rounded-lg shadow-sm dark:glass-effect bg-bgSecondary border border-gray-700/50 p-8">

        {/* Page title */}
        <div className="flex items-center gap-4 my-4 pb-2">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="flex items-center dark:text-gray-300 text-textSecondary hover:opacity-80 transition-opacity"
          >
            <ArrowLeft className="size-5" />
          </button>
          <p className="font-sans font-normal text-[20px] leading-[14px] tracking-[0] dark:text-[#FFFFFF]/90 text-textPrimary">
            Personal Profile
          </p>
        </div>

        <form className="pr-[150px] pl-[30px]" onSubmit={handlePersonalSubmit}>

          {/* Avatar block */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', paddingTop: '32px', paddingBottom: '28px' }}>
            <div style={{ position: 'relative', marginBottom: '14px' }}>
              {personalData.profileImage ? (
                <img
                  src={personalData.profileImage}
                  alt="Profile"
                  style={{ width: '90px', height: '90px', borderRadius: '50%', objectFit: 'cover', display: 'block' }}
                />
              ) : (
                <div style={{
                  width: '90px', height: '90px', borderRadius: '50%',
                  background: theme === 'dark' ? '#1e2433' : '#2d3348',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="44" height="44" viewBox="0 0 24 24" fill="none">
                    <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" fill="#6b7280" />
                  </svg>
                </div>
              )}
              <button
                type="button"
                onClick={() => personalFileInputRef.current.click()}
                style={{
                  position: 'absolute', bottom: '2px', right: '2px',
                  width: '28px', height: '28px', borderRadius: '50%',
                  background: 'linear-gradient(135deg, #FB54AE, #2F64FF)',
                  border: '2px solid white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', padding: 0,
                }}
              >
                <Camera size={13} color="#fff" />
              </button>
            </div>

            <p style={{ margin: '0 0 4px', fontSize: '22px', fontWeight: '500', color: theme === 'dark' ? '#fff' : '#111827' }}>
              {personalData.name || 'Your Name'}
            </p>

            <input
              type="file" accept="image/*" ref={personalFileInputRef} style={{ display: 'none' }}
              onChange={(e) => {
                if (e.target.files?.[0]) {
                  const file = e.target.files[0];
                  if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
                    toast.error("Please upload a PNG, JPG, or JPEG image.");
                    return;
                  }
                  if (file.size > 1 * 1024 * 1024) {
                    toast.error("File size exceeds 1MB");
                    return;
                  }
                  setPersonalData(p => ({ ...p, profileImage: URL.createObjectURL(file), profileImageFile: file, deleteProfileImage: false }));
                }
              }}
            />
          </div>

          <div style={{ borderTop: `1px solid ${theme === 'dark' ? 'rgba(75,85,99,0.5)' : '#e5e7eb'}` }} />

          {/* Full Name */}
          <Row label="Full Name" hint="Your display name" theme={theme}>
            <input
              type="text"
              value={personalData.name}
              required
              placeholder="Your full name"
              onChange={(e) => setPersonalData(p => ({ ...p, name: e.target.value }))}
              style={getInputStyle(theme)}
            />
          </Row>

          {/* Email — read only */}
          <Row label="Email Address" hint="Used for login & notifications" theme={theme}>
            <div>
              <div style={{
                ...getInputStyle(theme),
                display: 'flex', alignItems: 'center', gap: '10px',
                color: theme === 'dark' ? '#9ca3af' : '#6b7280',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme === 'dark' ? '#6b7280' : '#9ca3af'} strokeWidth="1.8">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="M2 7l10 7 10-7" />
                </svg>
                <span style={{ fontSize: '14px', fontFamily: 'inherit' }}>{personalData.email}</span>
              </div>
              <p style={{ margin: '6px 0 0 4px', fontSize: '12px', color: '#9ca3af' }}>
                Email cannot be changed. Contact support if needed.
              </p>
            </div>
          </Row>

          {/* Phone Number */}
          <Row label="Phone Number" hint="Optional contact number" last theme={theme}>
            <PhoneInput
              international
              defaultCountry={defaultCountry}
              value={personalData.contact}
              onChange={(val) => setPersonalData(p => ({ ...p, contact: val || '' }))}
              placeholder="+91 98765 43210"
              style={{
                ...getInputStyle(theme),
                display: 'flex', alignItems: 'center',
                paddingLeft: '12px', paddingTop: 0, paddingBottom: 0, paddingRight: 0,
              }}
              countryCallingCodeEditable={true}
              countrySelectComponent={({ value, iconComponent: Icon }) => (
                <div className="pointer-events-none flex items-center mr-1">
                  {Icon && <Icon country={value} />}
                </div>
              )}
            />
          </Row>

          {/* Status message */}
          {message.text && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '10px 14px', borderRadius: '6px', margin: '16px 0',
              background: message.type === 'success' ? '#f0fdf4' : '#fef2f2',
              border: `1px solid ${message.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
            }}>
              {message.type === 'success' ? <CheckCircle size={14} color="#16a34a" /> : <AlertCircle size={14} color="#dc2626" />}
              <p style={{ margin: 0, fontSize: '13px', color: message.type === 'success' ? '#15803d' : '#dc2626' }}>{message.text}</p>
            </div>
          )}

          {/* Action buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '28px', marginTop: '8px' }}>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="dark:text-gray-300 text-textSecondary rounded-full"
              style={{
                padding: '8px 18px', fontSize: '14px', fontWeight: '500',
                border: `1px solid ${theme === 'dark' ? 'rgba(75,85,99,0.5)' : '#d1d5db'}`,
                background: 'transparent', cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="button disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting && (
                <span style={{
                  width: '12px', height: '12px', borderRadius: '50%',
                  border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff',
                  display: 'inline-block', animation: 'spin 0.7s linear infinite',
                }} />
              )}
              <span>{isSubmitting ? 'Saving...' : 'Save Changes'}</span>
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default EditPersonalProfile;